import { useState, useEffect, useRef } from 'react';
import { Trash2, Loader2, Save } from 'lucide-react';
import { TabSkeleton } from "../BotManagement/BotManagementSkeleton";
import { useParams } from 'react-router-dom';
import { authService } from '../../../services/api';
import api from '../../../services/api';
import { useNotification } from '../../../components/Notifications/NotificationContext';
import { MediaManager } from '../../../components/MediaManager/MediaManager';
import TextForm from '../../../components/TextForm/TextForm';
import Toggle from '../../../components/UI/Toggle/Toggle';
import { validateFields } from '../../../utils/contentBlacklistValidator';
import { validateButtons } from '../../../utils/buttonValidator';
import './index.css';

const CTA_DEFAULT_TEXT = '🛒 Ver Planos';
const MAX_REDIRECT_BUTTONS = 3;
const SECONDARY_MAX = 1000;

const BUTTON_COLORS = [
    { label: 'Padrão', value: 'default', class: 'bg-gray-600' },
    { label: 'Verde', value: 'green', class: 'bg-green-600' },
    { label: 'Vermelho', value: 'red', class: 'bg-red-600' },
    { label: 'Azul', value: 'blue', class: 'bg-blue-600' },
];

export default function Welcome() {
    const { botId } = useParams();
    const { showError, showSuccess } = useNotification();
    const mediaRef = useRef();

    const [welcomeMessage, setWelcomeMessage] = useState('');
    const [sendMode, setSendMode] = useState('agrupado');
    const [welcomeMediaList, setWelcomeMediaList] = useState([]);

    // Regras do funil (start do fluxo padrão)
    const [secondaryEnabled, setSecondaryEnabled] = useState(false);
    const [secondaryMessage, setSecondaryMessage] = useState('');
    const [ctaEnabled, setCtaEnabled] = useState(false);
    const [ctaText, setCtaText] = useState(CTA_DEFAULT_TEXT);
    const [ctaColor, setCtaColor] = useState('default');
    const [buttons, setButtons] = useState([]); // botões de redirect (máx. 3)

    const [loading, setLoading] = useState(false);
    const [loadingPage, setLoadingPage] = useState(true);

    useEffect(() => {
        if (!botId) {
            showError('Bot não encontrado');
            return;
        }
        loadBotData();
    }, [botId]);

    const loadBotData = async () => {
        try {
            const { data: response } = await authService.bots.edit_bot(botId);

            if (response.success && response.bot) {
                const bot = response.bot;
                setWelcomeMessage(bot.welcome_message || '');
                setSendMode(bot.welcome_send_mode || 'agrupado');

                setSecondaryEnabled(!!bot.welcome_secondary_enabled);
                setSecondaryMessage(bot.welcome_secondary_message || '');
                setCtaEnabled(!!bot.welcome_cta_enabled);
                setCtaText(bot.welcome_cta_text || CTA_DEFAULT_TEXT);
                setCtaColor(bot.welcome_cta_color || 'default');
                setButtons(
                    Array.isArray(bot.buttons)
                        ? bot.buttons.map((b) => ({ text: b.text || '', url: b.url || '', color: b.color || 'default' }))
                        : []
                );

                // Prioriza welcome_media_list (novo) com fallback para campos legados
                if (bot.welcome_media_list && bot.welcome_media_list.length > 0) {
                    setWelcomeMediaList(bot.welcome_media_list);
                } else {
                    const legacyList = [];
                    if (bot.welcome_image_file_id) {
                        legacyList.push({ media_type: 'photo', file_id: bot.welcome_image_file_id, thumbnail_file_id: null });
                    }
                    if (bot.welcome_video_file_id) {
                        legacyList.push({ media_type: 'video', file_id: bot.welcome_video_file_id, thumbnail_file_id: null });
                    }
                    if (bot.welcome_audio_file_id) {
                        legacyList.push({ media_type: 'voice', file_id: bot.welcome_audio_file_id, thumbnail_file_id: null });
                    }
                    setWelcomeMediaList(legacyList);
                }
            }
        } catch (err) {
            console.error('Erro ao carregar bot:', err);
            showError('Erro ao carregar dados do bot');
        } finally {
            setLoadingPage(false);
        }
    };

    // ── Botões de redirect ───────────────────────────────────────────────
    const handleButtonChange = (index, field, value) => {
        setButtons((prev) => prev.map((b, i) => (i === index ? { ...b, [field]: value } : b)));
    };

    const addButton = () => {
        if (buttons.length >= MAX_REDIRECT_BUTTONS) return;
        setButtons([...buttons, { text: '', url: '', color: 'default' }]);
    };

    const removeButton = (index) => {
        setButtons(buttons.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (!welcomeMessage.trim()) {
                showError('Mensagem de boas-vindas é obrigatória');
                setLoading(false);
                return;
            }

            if (secondaryEnabled && !secondaryMessage.trim()) {
                showError('A mensagem secundária está ligada, mas está vazia');
                setLoading(false);
                return;
            }

            // Validação dos botões de redirect (mesmo validador do Buttons.jsx)
            const buttonValidation = validateButtons(buttons);
            if (!buttonValidation.isValid) {
                showError(buttonValidation.error);
                setLoading(false);
                return;
            }

            const contentCheck = validateFields([
                { label: 'Mensagem de Boas-vindas', value: welcomeMessage },
                ...(secondaryEnabled ? [{ label: 'Mensagem secundária', value: secondaryMessage }] : []),
                ...(ctaEnabled ? [{ label: 'Botão CTA', value: ctaText }] : []),
                ...buttonValidation.validButtons.map((btn, i) => ({ label: `Botão ${i + 1}`, value: btn.text })),
            ]);
            if (!contentCheck.isValid) {
                showError(contentCheck.error);
                setLoading(false);
                return;
            }

            const welcomeData = {
                welcome_message: welcomeMessage,
                welcome_send_mode: sendMode,
                welcome_secondary_enabled: secondaryEnabled,
                welcome_secondary_message: secondaryMessage,
                welcome_cta_enabled: ctaEnabled,
                welcome_cta_text: (ctaText || '').trim() || CTA_DEFAULT_TEXT,
                welcome_cta_color: ctaColor,
                buttons: buttonValidation.validButtons,
            };
            const { data: response } = await api.put(`/api/bots/${botId}/welcome`, welcomeData);

            if (!response.success) {
                throw new Error(response.error || 'Erro ao atualizar boas-vindas');
            }

            // Upload de mídias pendentes (diferido — chamado após salvar o texto)
            if (mediaRef.current) {
                await mediaRef.current.uploadAll();
            }

            showSuccess('Mensagem de boas-vindas atualizada com sucesso!');
        } catch (err) {
            console.error(err);
            const errorMsg = err?.response?.data?.error || err?.message || 'Erro ao atualizar boas-vindas';
            showError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    if (loadingPage) {
        return <TabSkeleton tab="welcome" />;
    }

    return (
        <form className="spt-reveal" onSubmit={handleSubmit} style={{ opacity: loading ? 0.6 : 1, pointerEvents: loading ? 'none' : 'auto' }}>
            <div className="welcome-layout">
                {/* ESQUERDA — form padrão (mídia + mensagem) */}
                <div className="spt-form-bot welcome-form welcome-main">
                    <div>
                        <MediaManager
                            ref={mediaRef}
                            botId={botId}
                            targetType="bot"
                            targetId={botId}
                            multiMedia={true}
                            mediaList={welcomeMediaList}
                            onMediaChange={setWelcomeMediaList}
                            sendMode={sendMode}
                            onSendModeChange={setSendMode}
                            uploadContext="welcome"
                        />
                    </div>

                    <div className="mt-8">
                        <label className="spt-label">
                            Mensagem de Boas-vindas
                            <span className="required">*</span>
                        </label>
                        <TextForm
                            value={welcomeMessage}
                            hint="Mensagem que será enviada quando usuários iniciarem conversa com /start"
                            onChange={setWelcomeMessage}
                        />
                    </div>
                </div>

                {/* DIREITA — 3 cards de regras do funil */}
                <aside className="welcome-side space-y-4">
                    {/* Card 1 — Mensagem secundária */}
                    <div className="welcome-card">
                        <div className="p-4">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#ec4899]" aria-hidden="true">
                                        <path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z" />
                                    </svg>
                                    <h3 className="font-semibold text-white text-sm">Mensagem Secundária</h3>
                                </div>
                                <Toggle
                                    id="welcome-secondary-toggle"
                                    className="toggle-sm"
                                    checked={secondaryEnabled}
                                    onChange={setSecondaryEnabled}
                                />
                            </div>
                            <p className="text-[11px] text-white/40 mb-3">
                                Balão separado onde os botões serão enviados.
                            </p>
                            {secondaryEnabled && (
                                <TextForm
                                    value={secondaryMessage}
                                    showVariables={false}
                                    hint={`${secondaryMessage.length}/${SECONDARY_MAX}`}
                                    onChange={(v) => setSecondaryMessage(v.slice(0, SECONDARY_MAX))}
                                    placeholder="Escolha um plano abaixo 👇"
                                />
                            )}
                        </div>
                    </div>

                    {/* Card 2 — Botão CTA */}
                    <div className="welcome-card">
                        <div className="p-4">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[var(--brand-500)]" aria-hidden="true">
                                        <path d="M15 3h6v6" />
                                        <path d="M10 14 21 3" />
                                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                    </svg>
                                    <h3 className="font-bold text-white text-sm">Botão CTA</h3>
                                </div>
                                <Toggle
                                    id="welcome-cta-toggle"
                                    className="toggle-sm"
                                    checked={ctaEnabled}
                                    onChange={setCtaEnabled}
                                />
                            </div>
                            <p className="text-[11px] text-white/40 mb-3">
                                Ao clicar, o botão some e mostra os planos do bot.
                            </p>
                            {ctaEnabled && (
                                <div className="space-y-3">
                                    <input
                                        className="input3 w-full text-sm"
                                        value={ctaText}
                                        maxLength={64}
                                        onChange={(e) => setCtaText(e.target.value)}
                                        placeholder={CTA_DEFAULT_TEXT}
                                    />
                                    <div className="flex items-center gap-2">
                                        <span className="text-[11px] text-white/40">Cor</span>
                                        {BUTTON_COLORS.map((c) => (
                                            <button
                                                key={c.value}
                                                type="button"
                                                title={c.label}
                                                onClick={() => setCtaColor(c.value)}
                                                className={`w-5 h-5 rounded-full border-2 transition-all ${c.class} ${ctaColor === c.value
                                                    ? 'border-white scale-110'
                                                    : 'border-white/20'}`}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Card 3 — Botões de redirect */}
                    <div className="welcome-card">
                        <div className="p-4">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#8b5cf6]" aria-hidden="true">
                                        <path d="M15 3h6v6" />
                                        <path d="M10 14 21 3" />
                                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                    </svg>
                                    <h3 className="font-bold text-white text-sm">Botões Redirect</h3>
                                    <span className="text-[11px] text-white/30">
                                        {buttons.length}/{MAX_REDIRECT_BUTTONS}
                                    </span>
                                </div>
                                {buttons.length < MAX_REDIRECT_BUTTONS && (
                                    <button
                                        type="button"
                                        onClick={addButton}
                                        className="text-[11px] text-[#8b5cf6] hover:text-[#a78bfa] transition-colors"
                                    >
                                        + Adicionar
                                    </button>
                                )}
                            </div>
                            <p className="text-[11px] text-white/40 mb-3">
                                Redirecionam para links externos (até {MAX_REDIRECT_BUTTONS}). Aparecem após os planos — ou abaixo do CTA.
                            </p>

                            <div className="space-y-3">
                                {buttons.length === 0 && (
                                    <p className="text-[11px] text-white/30">Nenhum botão configurado.</p>
                                )}
                                {buttons.map((button, index) => (
                                    <div
                                        key={index}
                                        className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.06] space-y-2"
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="text-[11px] text-white/40">Botão {index + 1}</span>
                                            <div className="flex items-center gap-2">
                                                <div className="flex items-center gap-1">
                                                    {BUTTON_COLORS.map((c) => (
                                                        <button
                                                            key={c.value}
                                                            type="button"
                                                            title={c.label}
                                                            onClick={() => handleButtonChange(index, 'color', c.value)}
                                                            className={`w-5 h-5 rounded-full border-2 ${c.class} ${button.color === c.value
                                                                ? 'border-white scale-110'
                                                                : 'border-white/20'} transition-all`}
                                                        />
                                                    ))}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => removeButton(index)}
                                                    className="text-red-400/60 hover:text-red-400 p-0.5"
                                                    title="Remover"
                                                >
                                                    <Trash2 size={12} />
                                                </button>
                                            </div>
                                        </div>
                                        <input
                                            className="input3 w-full text-sm"
                                            placeholder="📺 Ver Prévias"
                                            value={button.text}
                                            maxLength={64}
                                            required
                                            onChange={(e) => handleButtonChange(index, 'text', e.target.value)}
                                        />
                                        <input
                                            className="input3 w-full text-sm"
                                            placeholder="https://t.me/grupogratis"
                                            value={button.url}
                                            maxLength={200}
                                            required
                                            onChange={(e) => handleButtonChange(index, 'url', e.target.value)}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <button disabled={loading} className="btn-primary w-full block">
                        {loading ? (
                            <><Loader2 size={14} className="animate-spin me-2 inline" /> Salvando...</>
                        ) : (
                            <><Save size={14} className="me-2 inline" /> Salvar Boas-vindas</>
                        )}
                    </button>
                </aside>
            </div>
        </form>
    );
}
